// ResetChallengePage — Reset a challenge (per terms: anytime, keeps remaining days)
// Tier-priced resets: $69 / $129 / $189

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow } from '../../components/TfPrimitives';

interface ChallengeState {
  id: string;
  tier: 'Beginner' | 'Mid' | 'Professional';
  account: string;
  startDate: string;
  daysUsed: number;
  totalDays: number;
  progress: number; // 0-100
  currentPnl: number;
  profitTarget: number;
  status: 'active' | 'breached' | 'failed' | 'paused';
}

const MOCK_CHALLENGES: ChallengeState[] = [
  { id: 'c1', tier: 'Beginner',     account: '$50K',  startDate: '2026-07-04', daysUsed: 14, totalDays: 30, progress: 45, currentPnl: 1280, profitTarget: 3000, status: 'active' },
  { id: 'c2', tier: 'Mid',          account: '$100K', startDate: '2026-07-12', daysUsed: 8,  totalDays: 30, progress: 20, currentPnl: -340, profitTarget: 6000, status: 'active' },
  { id: 'c3', tier: 'Professional', account: '$150K', startDate: '2026-07-01', daysUsed: 23, totalDays: 30, progress: 78, currentPnl: 4280, profitTarget: 9000, status: 'active' },
  { id: 'c4', tier: 'Beginner',     account: '$50K',  startDate: '2026-06-12', daysUsed: 30, totalDays: 30, progress: 12, currentPnl: -1240, profitTarget: 3000, status: 'breached' },
];

const RESET_PRICE: Record<string, number> = { Beginner: 69, Mid: 129, Professional: 189 };

export function ResetChallengePage() {
  const nav = useNavigate();
  const { id } = useParams();
  const [challenges, setChallenges] = useState(MOCK_CHALLENGES);
  const [selected, setSelected] = useState<string | null>(id || 'c1');
  const [confirming, setConfirming] = useState(false);
  const challenge = challenges.find((c) => c.id === selected) || challenges[0];

  function reset(challengeId: string) {
    setChallenges((curr) => curr.map((c) => {
      if (c.id !== challengeId) return c;
      const daysUsed = 0;
      return { ...c, daysUsed, progress: 0, currentPnl: 0, startDate: new Date().toISOString().slice(0, 10), status: 'active' as const };
    }));
    setConfirming(false);
    alert('Challenge reset. P&L cleared. New challenge started.');
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Reset challenge</div>
        <div className="w-9" />
      </div>

      {/* Hero — white floating card with massive reset price + pill buttons */}
      <div className="mx-4 mt-4">
        <TfCardFloating className="text-center">
          <div className="text-[13px]" style={{ color: '#8E8E93' }}>Bankable reset</div>
          <div className="mt-1 flex items-baseline justify-center gap-1">
            <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: '#1C1C1E' }}>${RESET_PRICE[challenge.tier]}</span>
          </div>
          <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
            <span className="text-tf-text-3">resets your {challenge.account} challenge</span>
          </div>
          <p className="mt-1.5 text-[11px] text-tf-text-3 max-w-xs mx-auto">Keeps your remaining days. Resets P&L to 0. Wipes all open trades.</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={() => setConfirming(true)} data-testid="hero-confirm-reset" className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Pay
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7" /><path d="M7 7h10v10" />
              </svg>
            </button>
            <button onClick={() => nav(-1)} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Cancel
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 7L7 17" /><path d="M17 17H7V7" />
              </svg>
            </button>
          </div>
        </TfCardFloating>
      </div>

      {/* Stat row: days used, days remaining, price */}
      <TfStatRow
        className="mt-4"
        stats={[
          { label: 'Days used', value: `${challenge.daysUsed}`, color: '#1C1C1E' },
          { label: 'Remaining', value: `${challenge.totalDays - challenge.daysUsed}`, color: '#34C759' },
          { label: 'Reset fee', value: `$${RESET_PRICE[challenge.tier]}`, color: '#007AFF' },
        ]}
      />

      {/* Challenge list */}
      <TfSectionHeaderView title="Select a challenge to reset" />
      <div className="mx-4">
        <TfCard>
          {challenges.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelected(c.id)}
              data-testid={`reset-${c.id}`}
              className={['flex w-full items-center gap-3 px-4 py-3 border-b border-tf-border last:border-b-0 active:bg-tf-surface-1 transition text-left',
                selected === c.id ? 'bg-tf-blue/[0.05]' : ''
              ].join(' ')}
            >
              <div className={['flex h-10 w-10 items-center justify-center rounded-[10px] text-[16px] font-bold',
                c.status === 'breached' ? 'bg-tf-red-tint text-tf-loss' : 'bg-tf-blue/15 text-tf-blue'].join(' ')}>
                {c.tier === 'Beginner' ? '🌱' : c.tier === 'Mid' ? '🚀' : '💎'}
              </div>
              <div className="flex-1 text-left">
                <div className="text-[14px] font-semibold text-tf-text-1">{c.tier} · {c.account}</div>
                <div className="text-[11px] text-tf-text-3">Started {c.startDate} · {c.daysUsed}/{c.totalDays} days used</div>
                <div className="mt-0.5 text-[11px]">
                  {c.currentPnl >= 0 ? <span className="text-tf-profit font-semibold">+${c.currentPnl.toLocaleString()}</span> : <span className="text-tf-loss font-semibold">${c.currentPnl.toLocaleString()}</span>}
                  <span className="text-tf-text-3"> / ${c.profitTarget.toLocaleString()} target</span>
                </div>
              </div>
              <div className="text-right">
                <div className={['rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase',
                  c.status === 'active' ? 'bg-tf-green-tint text-tf-profit' :
                  c.status === 'breached' ? 'bg-tf-red-tint text-tf-loss' :
                  'bg-tf-orange-tint text-tf-warning'
                ].join(' ')}>{c.status}</div>
                {selected === c.id && <svg viewBox="0 0 24 24" className="ml-auto h-4 w-4 text-tf-blue mt-1" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>}
              </div>
            </button>
          ))}
        </TfCard>
      </div>

      {/* What gets reset */}
      <TfSectionHeaderView title="What happens on reset" />
      <div className="mx-4">
        <TfCard>
          {[
            { label: 'Account balance',      action: 'Reset to $0 starting balance', icon: '💰' },
            { label: 'Open positions',       action: 'All closed at market',            icon: '📉' },
            { label: 'P&L history',          action: 'Wiped (stats stay in profile)',   icon: '📊' },
            { label: 'Days used',            action: 'Reset to 0 (full 30 days back)',  icon: '📅' },
            { label: 'Trading days counted', action: 'Kept (for consistency rule)',     icon: '🔢' },
            { label: 'Profit target',        action: 'Reset to 6% of account',          icon: '🎯' },
            { label: 'Drawdown tracking',    action: 'Reset to initial',                icon: '📉' },
          ].map((item, i, a) => (
            <div key={item.label} data-testid={`reset-${item.label}`} className={['flex items-center gap-3 px-4 py-3', i < a.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
              <div className="text-[20px]">{item.icon}</div>
              <div className="flex-1 text-[13px] font-semibold text-tf-text-1">{item.label}</div>
              <div className="text-[12px] text-tf-text-3 text-right">{item.action}</div>
            </div>
          ))}
        </TfCard>
      </div>

      {/* CTA */}
      <div className="mx-4 mt-4">
        <button onClick={() => setConfirming(true)} data-testid="confirm-reset" className="flex h-12 w-full items-center justify-center btn-spring">
          Reset {challenge.tier} {challenge.account} · ${RESET_PRICE[challenge.tier]}
        </button>
        <p className="mt-2 text-center text-[10px] text-tf-text-3">72-hour refund window · No questions asked</p>
      </div>

      {/* Confirm sheet */}
      {confirming && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm" onClick={() => setConfirming(false)}>
          <div className="ios26-sheet w-full max-w-[420px]" onClick={(e) => e.stopPropagation()}>
            <h2 className="px-4 pb-2 text-[20px] font-bold tracking-tight text-tf-text-1">Confirm reset</h2>
            <p className="px-4 pb-3 text-[13px] text-tf-text-3">You're about to reset your {challenge.tier} {challenge.account} challenge for ${RESET_PRICE[challenge.tier]}.</p>
            <div className="ios26-list bg-white">
              <div className="px-4 py-3 flex justify-between"><span className="text-[12px] text-tf-text-3">Account</span><span className="text-[14px] font-semibold text-tf-text-1">{challenge.tier} {challenge.account}</span></div>
              <div className="px-4 py-3 flex justify-between border-t border-tf-border"><span className="text-[12px] text-tf-text-3">Days remaining</span><span className="text-[14px] font-semibold text-tf-text-1">{challenge.totalDays - challenge.daysUsed} of {challenge.totalDays}</span></div>
              <div className="px-4 py-3 flex justify-between border-t border-tf-border"><span className="text-[12px] text-tf-text-3">Reset fee</span><span className="text-[14px] font-bold text-tf-blue">${RESET_PRICE[challenge.tier]}</span></div>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setConfirming(false)} className="flex-1 h-12 rounded-full bg-tf-surface-1 text-[15px] font-semibold text-tf-text-1 active:opacity-40">Cancel</button>
              <button onClick={() => reset(challenge.id)} data-testid="pay-reset" className="flex-1 h-12 btn-spring">Pay & reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
